import { useParams } from 'react-router-dom';

import { snapStore } from 'containers/Snaps/Snap/store';
import { CRUDTable } from 'components';

import {
  useDeleteRequest,
  useGetRequest,
  useMemoState,
  usePatchRequest,
  usePostRequest,
} from 'hooks';

import {
  ProductLineItemMatch,
  ProductLineItemMatchUpdate,
  ProductMatchWithSnapState,
  SnapProductMatchesResponse,
} from 'codegen';

import {
  columns,
  handleDeleteMatchesLineItem,
  handlePatchMatchesLineItem,
  handlePostMatchesLineItem,
  handleSuccessDeleteMatchesLineItem,
  handleSuccessGetMatchesLineItems,
  handleSuccessPatchMatchesLineItem,
  handleSuccessPostMatchesLineItem,
  initialTableActions,
} from './lib';
import { ProductMatchesFieldsChange } from './ProductMatchesFieldsChange';
import { RematchButton } from './RematchButton';

export const ProductMatches = () => {
  const lineItemOptions = snapStore((state) => state.lineItemOptions);
  const eligibleProductOptions = snapStore((state) => state.eligibleProductOptions);
  const productIdentitiesOptions = snapStore((state) => state.productIdentitiesOptions);

  const { snapId } = useParams();

  const [matchesData, setMatchesData] = useMemoState<ProductLineItemMatch[]>([]);
  const [matchesStatuses, setMatchesStatuses] = useMemoState<Omit<SnapProductMatchesResponse, 'matchedItems'>>();

  const { loading, refresh } = useGetRequest<SnapProductMatchesResponse>({
    url: `/snaps/${snapId}/matches`,
    options: {
      onSuccess: handleSuccessGetMatchesLineItems(setMatchesData, setMatchesStatuses),
      refreshDeps: [snapId],
    },
  });

  const [postMatchesItem, { loading: postLoading }] = usePostRequest<ProductMatchWithSnapState, ProductLineItemMatchUpdate>({
    url: `/snaps/${snapId}/matches`,
    options: {
      onSuccess: handleSuccessPostMatchesLineItem(refresh),
    },
  });

  const [patchMatchesItem, { loading: patchLoading }] = usePatchRequest<ProductMatchWithSnapState, ProductLineItemMatchUpdate>({
    options: {
      onSuccess: handleSuccessPatchMatchesLineItem(refresh),
    },
  });

  const [deleteMatchesItem, { loading: deleteLoading }] = useDeleteRequest({
    options: {
      onSuccess: handleSuccessDeleteMatchesLineItem(refresh),
    },
  });

  const isLoading = loading || patchLoading || postLoading || deleteLoading;

  return (
    <CRUDTable
      id='product-matches'
      title='Product Matches'
      loading={isLoading}
      dataSource={matchesData}
      addNewBtn={{ visible: !!matchesStatuses?.lineItemMatchesCreationAllowed }}
      initialTableActions={initialTableActions}
      onPostTableData={handlePostMatchesLineItem(postMatchesItem)}
      onPatchTableData={handlePatchMatchesLineItem(patchMatchesItem, snapId)}
      onDeleteTableData={handleDeleteMatchesLineItem(deleteMatchesItem, snapId)}
      columns={columns({
        lineItemOptions,
        eligibleProductOptions,
        productIdentitiesOptions,
      })}
      getRowClassName={(row) => {
        if (Object.hasOwn(row, 'isMarkedForDeletion') && row.isMarkedForDeletion) {
          return '[&_*]:text-[#FF0000]/40';
        }

        return '';
      }}
      titleExtraNode={(
        <RematchButton
          isRematchAllowed={matchesStatuses?.rematchAllowed}
          setMatchesData={setMatchesData}
        />
      )}
    >
      <ProductMatchesFieldsChange />
    </CRUDTable>
  );
};
